import { defineStore } from 'pinia';
import { apiService } from '../services/apiService';
import type { App, CreateAppRequest, UpdateAppRequest, ApiError } from '../types/api';

interface AppState {
  apps: App[];
  currentApp: App | null;
  loading: boolean;
  error: string | null;
}

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    apps: [],
    currentApp: null,
    loading: false,
    error: null
  }),

  getters: {
    getAppById: (state) => (id: string): App | undefined => {
      return state.apps.find((app: App) => app.id === id);
    },
    getAppBySlug: (state) => (slug: string): App | undefined => {
      return state.apps.find((app: App) => app.slug === slug);
    },
    publishedApps: (state): App[] => {
      return state.apps.filter((app: App) => app.isPublished);
    },
    unpublishedApps: (state): App[] => {
      return state.apps.filter((app: App) => !app.isPublished);
    }
  },

  actions: {
    // Fetch all user's apps
    async fetchApps(): Promise<void> {
      this.loading = true;
      this.error = null;

      try {
        this.apps = await apiService.getApps();
      } catch (error) {
        this.error = (error as ApiError).error || 'Failed to fetch apps';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Create new app
    async createApp(appData: CreateAppRequest): Promise<App> {
      this.loading = true;
      this.error = null;

      try {
        const newApp = await apiService.createApp(appData);
        this.apps.unshift(newApp);
        return newApp;
      } catch (error) {
        this.error = (error as ApiError).error || 'Failed to create app';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Update app
    async updateApp(appId: string, updates: UpdateAppRequest): Promise<App> {
      this.loading = true;
      this.error = null;

      try {
        const updatedApp = await apiService.updateApp(appId, updates);
        
        // Update in the apps array
        const index = this.apps.findIndex((app: App) => app.id === appId);
        if (index !== -1) {
          this.apps[index] = updatedApp;
        }
        
        // Update current app if it's the same
        if (this.currentApp?.id === appId) {
          this.currentApp = updatedApp;
        }
        
        return updatedApp;
      } catch (error) {
        this.error = (error as ApiError).error || 'Failed to update app';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Delete app
    async deleteApp(appId: string): Promise<void> {
      this.loading = true;
      this.error = null;

      try {
        await apiService.deleteApp(appId);
        
        // Remove from apps array
        this.apps = this.apps.filter((app: App) => app.id !== appId);
        
        // Clear current app if it's the same
        if (this.currentApp?.id === appId) {
          this.currentApp = null;
        }
      } catch (error) {
        this.error = (error as ApiError).error || 'Failed to delete app';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Set current app
    setCurrentApp(app: App | null): void {
      this.currentApp = app;
    },

    // Set current app by ID
    async setCurrentAppById(appId: string): Promise<void> {
      const app = this.getAppById(appId);
      if (app) {
        this.currentApp = app;
      } else {
        // If app not in store, try to fetch it
        await this.fetchApps();
        const fetchedApp = this.getAppById(appId);
        this.currentApp = fetchedApp || null;
      }
    },

    // Clear error
    clearError(): void {
      this.error = null;
    }
  }
}); 